import { router } from "@inertiajs/react";
import { Checkbox, Divider, FormControlLabel, FormGroup, Grid } from "@mui/material";
import { useIsEligibilityReadOnly } from "app/frontend/components/contact/useIsEligibilityReadOnly";
import BaseEligibilityAccordion from "app/frontend/components/eligibility/accordions/BaseEligibilityAccordion";
import { IEligibilityProps } from "app/frontend/components/eligibility/accordions/types";
import { EEligibilityStep } from "app/frontend/components/eligibility/constants";
import { useOnContinue } from "app/frontend/components/eligibility/hooks/useOnContinue";
import { EVoucherProgram } from "app/frontend/constants/mhd";
import { ROUTES } from "app/frontend/constants/routes";
import { useParams } from "app/frontend/hooks/useParams";
import { useParamsVoucherNames, useProjectParams } from "app/frontend/hooks/usePropsParams";
import { Fragment, useCallback, useMemo } from "react";

const ProjectVouchersAccordion: React.FC<IEligibilityProps> = () => {
    const readOnly = useIsEligibilityReadOnly();

    const params = useParams();
    const projectParams = useProjectParams();
    const onContinue = useOnContinue();

    const voucherNames = useParamsVoucherNames();

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (readOnly) return;

            const newVoucherName = e.target.id as keyof typeof EVoucherProgram;

            const newVoucherNames = (() => {
                // Remove the voucher if it is already selected
                if (voucherNames.includes(newVoucherName)) {
                    return voucherNames.filter((v) => v !== newVoucherName);
                }

                // Users should not be able to select both a voucher program option(s) AND one of the "i don't know"s below
                else if (["NO_PARTICIPATION", "UNKNOWN"].includes(newVoucherName)) {
                    return [newVoucherName];
                }

                // Add the new voucher to the list
                else {
                    const _newVouchers = [...voucherNames, newVoucherName];
                    return _newVouchers.filter((v) => {
                        if (!v) return false;

                        // Remove the "I don't know checkbox, if one is selected"
                        if (["NO_PARTICIPATION", "UNKNOWN"].includes(v)) {
                            return false;
                        }

                        return true;
                    });
                }
            })();

            router.get(
                ROUTES.Eligibility.ROOT,
                {
                    ...params,
                    project: {
                        ...projectParams,
                        voucher_names: newVoucherNames,
                    } as Record<string, any>,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                },
            );
        },
        [params, projectParams, voucherNames, readOnly],
    );

    const header = useMemo(() => {
        if (voucherNames?.length) {
            if (voucherNames.length === 1) {
                if (["NO_PARTICIPATION", "UNKNOWN"].includes(voucherNames.first())) {
                    return "No Voucher Program";
                } else {
                    return `${voucherNames.first().split("_").join(" ")} Voucher Program`;
                }
            } else {
                return `${voucherNames.length} Voucher Programs`;
            }
        } else {
            return "Voucher Programs";
        }
    }, [voucherNames]);

    return (
        <BaseEligibilityAccordion
            header={header}
            h2="Do you currently participate in any of the following voucher Incentive programs?"
            h4="Participating in a Voucher Incentive Program can unlock additional MHD Pilot incentives."
            onContinue={onContinue}
            step={EEligibilityStep.VOUCHERS}
            isContinueDisabled={!voucherNames?.length}
        >
            <Grid container spacing={0} sx={{ mt: 2 }}>
                <Grid size={{ xs: 0, md: 1, lg: 2 }}>&nbsp;</Grid>
                <Grid size={{ xs: 12, md: 10, lg: 8 }}>
                    <FormGroup sx={{ alignItems: "start", gap: { xs: 2, md: 0 } }}>
                        {Object.keys(EVoucherProgram).map((_voucherName) => {
                            const voucherName = _voucherName as keyof typeof EVoucherProgram;
                            return (
                                <Fragment key={voucherName}>
                                    {voucherName === "NO_PARTICIPATION" && <Divider sx={{ width: "100%", my: 2 }} />}
                                    <FormControlLabel
                                        sx={{ fontSize: "14px", fontWeight: 400 }}
                                        control={
                                            <Checkbox
                                                id={voucherName}
                                                checked={voucherNames.includes(voucherName)}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                            />
                                        }
                                        label={EVoucherProgram[voucherName]}
                                        slotProps={{
                                            typography: {
                                                sx: {
                                                    "&.MuiTypography-root": {
                                                        fontSize: { xs: "14px", md: "16px" },
                                                        fontWeight: 400,
                                                        lineHeight: "normal",
                                                        fontStyle: "normal",
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Fragment>
                            );
                        })}
                    </FormGroup>
                </Grid>
                <Grid size={{ xs: 0, md: 1, lg: 2 }}>&nbsp;</Grid>
            </Grid>
        </BaseEligibilityAccordion>
    );
};

export default ProjectVouchersAccordion;
